import type { Row } from "@rocicorp/zero";
import { useZero } from "@rocicorp/zero/react";
import { keyframes, styled } from "goober";
import { nanoid } from "nanoid";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { workflowAgentFiles } from "../../../shared/agentDocs";
import { buildFileMap } from "../../../shared/buildFileMap";
import { supportsManualGitWebhook } from "../../../shared/connectors/gitAuthIdentifiers";
import { sharedMutators } from "../../../shared/mutators";
import { GIT_SYNC_STALE_MS } from "../../../shared/mutators/syncGit";
import { queries } from "../../../shared/queries";
import { useDeleteLastBranch } from "../../hooks/useDeleteLastBranch";
import { useHasAccess } from "../../hooks/useHasAccess";
import { useIsGitSynced } from "../../hooks/useIsGitSynced";
import { useMouseDownOutside } from "../../hooks/useMouseDownOutside";
import { usePushLive } from "../../hooks/usePushLive";
import { useQuery } from "../../hooks/useQuery";
import { branchLastActivity } from "../../lib/branch";
import { resolveDefaultPlanMode, savePlanMode } from "../../lib/planMode";
import { spaceGitPath } from "../../lib/apiPaths";
import { apiUrl } from "../../lib/tenantOrigin";
import { downloadBlob, downloadChatAsJson } from "../../lib/downloadChat";
import { playClick, playPushLive } from "../../lib/sounds";
import { makeUrl, navigate, routes } from "../../lib/urls";
import { userDisplayName } from "../../lib/userDisplayName";
import { writeToClipboard } from "../../lib/writeToClipboard";
import { zIndexes } from "../../lib/zIndexes";
import { createZip } from "../../lib/zip";
import { AnchoredPopover } from "../AnchoredPopover";
import { Avatar } from "../Avatar";
import { ClickButton } from "../ClickButton";
import { ConfirmModal } from "../ConfirmModal";
import { FacePile } from "../FacePile";
import { Icon } from "../Icon";
import { Link } from "../Link";
import { LiveIndicator } from "../LiveIndicator";
import { MenuItem } from "../MenuItem";
import { StatusIndicator } from "../StatusIndicator";
import { StreamingText } from "../StreamingText";
import { META_SEP } from "../MetaSeparator";
import { Text } from "../Text";
import { PushLivePreflight } from "../PushLivePreflight";
import { useToast } from "../Toast";
import { Tooltip } from "../Tooltip";
import type {
  BranchData,
  BranchSummary,
  ChatEntryData,
  WorkflowData,
} from "../Workflow";

type Props = {
  workflow: Pick<
    WorkflowData,
    "id" | "name" | "autofixBranchId" | "autoImproveBranchId" | "pausedAt"
  >;
  currentBranch: BranchData;
  branchSummaries: readonly BranchSummary[];
  commitId: string | null;
  spaceId: string;
  presentBranchesByUser: Map<string, Set<string>>;
  chatEntries: readonly ChatEntryData[];
  hasChangesToPush: boolean;
  canWrite: boolean;
  canPushLive: boolean;
  isDeletedOnBranch: boolean;
  isDeploying: boolean;
  isMerging: boolean;
  onDeploying: (v: boolean) => void;
  onMerging: (v: boolean) => void;
};

function getContributors(branch: BranchSummary | BranchData): Row["user"][] {
  return branch.contributors.flatMap((c) => (c.user ? [c.user] : []));
}

async function downloadCodeAsZip(
  z: Parameters<typeof buildFileMap>[0],
  commitId: string,
  workflowName: string,
  externalApiUrl: string
) {
  const fileMap = await buildFileMap(z, commitId);
  const safeName = workflowName.replace(/[/\\]/g, "-");
  const zipFiles: Record<string, string> = {};
  for (const [path, content] of Object.entries(fileMap)) {
    zipFiles[`${safeName}/${path}`] = content;
  }
  const agentFiles = workflowAgentFiles({
    uiUrl: window.location.origin,
    apiUrl: externalApiUrl,
  });
  for (const [name, content] of Object.entries(agentFiles)) {
    zipFiles[`${safeName}/${name}`] = content;
  }
  downloadBlob(createZip(zipFiles), `${safeName}.zip`);
}

type OpenPopover = "none" | "dropdown" | "actions";
type DropdownMode = "versions" | "history";

function formatVersionDate(timestamp: number): {
  prefix: string;
  date: string;
} {
  const date = new Date(timestamp);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  const timeStr = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  if (isToday) {
    return { prefix: "", date: `today, ${timeStr}` };
  }

  return {
    prefix: "",
    date: `${date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    })}, ${timeStr}`,
  };
}

const Wrapper = styled("div")`
  display: flex;
  align-items: center;
  gap: 0;
  margin-left: -12px;
  margin-right: -12px;
  padding: 12px;
  border-bottom: 0.5px solid var(--border-alpha);
`;

const SelectorBar = styled("button")`
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: center;
  height: 32px;
  padding: 0 4px 0 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  gap: 8px;
  border-radius: 8px !important;
  &:hover {
    background: var(--alpha4);
  }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const HistoryIndicator = styled("div")`
  position: relative;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--orange-400);
  flex-shrink: 0;

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--orange-600);
    animation: ${pulse} 2s ease-in-out infinite;
  }
`;

const SelectorContent = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex: 1;
  flex-wrap: nowrap;
  & > *:not(:first-child) {
    flex-shrink: 0;
  }
`;

const ViewingBadge = styled("span")`
  height: 16px;
  padding: 0 6px;
  border-radius: 8px;
  font-size: 9px;
  font-weight: 500;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  background: var(--sky-400);
  color: var(--sky-1000);
`;

const AutofixBadge = styled("span")`
  height: 16px;
  padding: 0 6px;
  border-radius: 8px;
  font-size: 9px;
  font-weight: 500;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  background: var(--orange-400);
  color: var(--orange-1000);
`;

const AutoImproveBadge = styled(AutofixBadge)`
  background: var(--purple-400);
  color: var(--purple-1000);
`;

const MenuDivider = styled("div")`
  height: 0.5px;
  margin: 4px 8px;
  background: var(--border-alpha);
`;

const MoreButton = styled("div")`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  outline: none;
  background: transparent;
  border-radius: 9999px;
  cursor: pointer;
  flex-shrink: 0;
  margin-left: auto;
  &:hover {
    background: var(--alpha4);
  }
`;

const pushLiveIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.85);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const PushLiveButton = styled("div")`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 4px;
  height: 20px;
  padding: 0 8px 0 6px;
  border: none;
  outline: none;
  background: var(--green-600);
  border-radius: 38px;
  color: white;
  cursor: pointer;
  flex-shrink: 0;
  transform-origin: center;
  animation: ${pushLiveIn} 100ms ease-out;
  &:hover {
    opacity: 0.8;
  }
`;

const SyncButton = styled(ClickButton)`
  position: relative;
  z-index: 1;
  align-items: center;
  gap: 4px;
  height: 20px;
  padding: 0 8px 0 6px;
  background: var(--green-600);
  border-radius: 38px;
  color: white;
  flex-shrink: 0;
  transform-origin: center;
  animation: ${pushLiveIn} 100ms ease-out;
  &:hover:not(:disabled) {
    opacity: 0.8;
  }
`;

// GitHub's /compare/<branch> defaults the base to the repository's default
// branch; slashes in the ref must stay literal for GitHub to resolve it.
function createPullRequestUrl(remoteUrl: string, gitBranchName: string) {
  let base: URL;
  try {
    base = new URL(remoteUrl.replace(/\/+$/, "").replace(/\.git$/, ""));
  } catch {
    return null;
  }
  const isGitlab = base.hostname.includes("gitlab");
  const href = isGitlab
    ? `${base}/-/merge_requests/new?merge_request%5Bsource_branch%5D=${encodeURIComponent(gitBranchName)}`
    : `${base}/compare/${gitBranchName.split("/").map(encodeURIComponent).join("/")}?expand=1`;
  return { href, isGitlab };
}

const PrBadge = styled("a")`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 20px;
  padding: 0 8px 0 6px;
  border-radius: 38px;
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  color: var(--text-secondary);
  text-decoration: none;
  flex-shrink: 0;
  &:hover {
    opacity: 0.8;
  }
`;

const ResumeButton = styled("div")`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 4px;
  height: 20px;
  padding: 0 8px 0 6px;
  border: none;
  outline: none;
  background: var(--alpha4);
  border-radius: 38px;
  color: var(--text-primary);
  cursor: pointer;
  flex-shrink: 0;
  transform-origin: center;
  animation: ${pushLiveIn} 100ms ease-out;
  &:hover {
    background: var(--alpha8);
  }
`;

const DropdownPopover = styled(AnchoredPopover)`
  top: anchor(bottom);
  left: anchor(left);
  right: anchor(right);
  margin-top: 4px;
  background: var(--surface-primary);
  box-shadow: var(--elevation-high);
  outline: 0.5px solid var(--border-alpha);
  padding: 4px;
  z-index: ${zIndexes.branchSelectorDropdown};
  max-height: 400px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const DropdownHeader = styled("div")`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  height: 32px;
  padding: 0 8px;
  color: var(--text-secondary);
`;

const versionRowStyles = `
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  border: 0;
  outline: 0;
  text-decoration: none;
  color: inherit;
  background: transparent;
  &:hover, &[data-selected="true"], &[data-highlighted="true"] {
    background: var(--alpha4);
  }
`;

const VersionRow = styled("button")`
  ${versionRowStyles}
`;

const VersionRowLink = styled(Link)`
  ${versionRowStyles}
`;

const VersionRowContent = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex: 1;
  flex-wrap: nowrap;
  & > *:not(:first-child) {
    flex-shrink: 0;
  }
`;

const ActionPopover = styled(AnchoredPopover)`
  top: anchor(bottom);
  right: anchor(right);
  margin-top: 4px;
  background: var(--surface-primary);
  box-shadow: var(--elevation-high);
  outline: 0.5px solid var(--border-alpha);
  padding: 4px;
  z-index: ${zIndexes.branchSelectorActions};
  min-width: 160px;
`;

export function BranchSelector({
  workflow,
  currentBranch,
  branchSummaries,
  commitId,
  spaceId,
  presentBranchesByUser: presentUserBranches,
  chatEntries,
  hasChangesToPush,
  canWrite,
  canPushLive,
  isDeletedOnBranch,
  isDeploying,
  isMerging,
  onDeploying,
  onMerging,
}: Props) {
  const z = useZero();
  const externalApiUrl = apiUrl();
  const [preferences] = useQuery(queries.userPreferences());
  const planModeBranchDefault = resolveDefaultPlanMode(preferences, "branch");
  const isGitSynced = useIsGitSynced(spaceId);
  const [gitRemote] = useQuery(queries.loadSpaceGitRemote({ spaceId }));
  const [allSpaces] = useQuery(queries.spacesLite({}));
  const spaceSlug = allSpaces?.find((s) => s.id === spaceId)?.slug ?? spaceId;
  const [openPrs] = useQuery(
    queries.openPullRequestsForBranch({ workflowBranchId: currentBranch.id })
  );
  const openPr = openPrs?.[0];
  const [spaceGitBranch] = useQuery(
    queries.gitBranchForWorkflowBranch({ workflowBranchId: currentBranch.id })
  );
  const canManageSpace = useHasAccess("space:manage", spaceId);
  const canSync = canPushLive || canManageSpace;
  const isSyncing =
    gitRemote?.syncStartedAt != null &&
    Date.now() - gitRemote.syncStartedAt < GIT_SYNC_STALE_MS;
  // Without a delivery webhook we never learn the PR was opened, so the
  // create link would dangle forever; manual-webhook providers must have one
  // configured before we offer it.
  const remoteConnector = gitRemote?.appUsage?.connectedApp;
  const webhookReady =
    remoteConnector != null &&
    (!supportsManualGitWebhook(remoteConnector.identifier) ||
      Boolean(remoteConnector.configuration.webhookSecretSetAt));
  const { showToast } = useToast();
  const [openPopover, setOpenPopover] = useState<OpenPopover>("none");
  const [dropdownMode, setDropdownMode] = useState<DropdownMode>("versions");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const popoverAnchorName = useRef(`--version-selector-${nanoid()}`);
  const actionAnchorName = useRef(`--action-menu-${nanoid()}`);

  const [containerRef, popoverRef] = useMouseDownOutside(() =>
    setOpenPopover("none")
  );
  const currentBranchId = currentBranch.id;

  // Sort branches: main first, then other branches by most recent activity
  const sortedBranches = useMemo(() => {
    const main = branchSummaries.find((b) => b.name === "main");
    const otherBranches = branchSummaries
      .filter((b) => b.name !== "main")
      .sort((a, b) => branchLastActivity(b) - branchLastActivity(a));
    return [...(main ? [main] : []), ...otherBranches];
  }, [branchSummaries]);

  const isMainBranch = currentBranch.name === "main";
  const isCurrentBranchAutofix = workflow.autofixBranchId === currentBranch.id;
  const isCurrentBranchAutoImprove =
    workflow.autoImproveBranchId === currentBranch.id;
  const mainBranch = branchSummaries.find((b) => b.name === "main");

  const [downloadingCode, setDownloadingCode] = useState(false);
  const [pauseConfirmOpen, setPauseConfirmOpen] = useState(false);
  const [preflightOpen, setPreflightOpen] = useState(false);
  const deleteLastBranch = useDeleteLastBranch();
  const isPaused = workflow.pausedAt != null;

  const handleConfirmPause = () => {
    setPauseConfirmOpen(false);
    setOpenPopover("none");
    z.mutate(sharedMutators.pauseWorkflow({ workflowId: workflow.id }));
  };

  const handleResume = () => {
    setOpenPopover("none");
    playPushLive();
    z.mutate(sharedMutators.unpauseWorkflow({ workflowId: workflow.id }));
  };

  useEffect(() => {
    if (!downloadingCode) return;
    const targetCommitId = commitId || currentBranch.headCommitId;
    if (!targetCommitId) {
      setDownloadingCode(false);
      return;
    }
    void downloadCodeAsZip(
      z,
      targetCommitId,
      workflow.name,
      externalApiUrl
    ).finally(() => setDownloadingCode(false));
  }, [
    downloadingCode,
    commitId,
    currentBranch.headCommitId,
    workflow.name,
    z,
    externalApiUrl,
  ]);

  const getBranchUrl = useCallback(
    (branchId: string) =>
      makeUrl(routes.space.workflow, {
        spaceId,
        workflowId: workflow.id,
        branchId,
        commitId: "",
      }),
    [spaceId, workflow.id]
  );

  const handleSelectBranch = useCallback(
    (branchId: string) => {
      navigate(routes.space.workflow, {
        spaceId,
        workflowId: workflow.id,
        branchId,
        commitId: "",
      });
      setOpenPopover("none");
    },
    [spaceId, workflow.id]
  );

  // Keyboard shortcut: Shift+Option+V to open version dropdown
  const handleGlobalKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.shiftKey && e.altKey && e.code === "KeyV") {
        e.preventDefault();
        setOpenPopover((prev) => (prev === "dropdown" ? "none" : "dropdown"));
        setHighlightedIndex(
          sortedBranches.findIndex((b) => b.id === currentBranchId)
        );
      }
    },
    [sortedBranches, currentBranchId]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleGlobalKeyDown);
    return () => document.removeEventListener("keydown", handleGlobalKeyDown);
  }, [handleGlobalKeyDown]);

  // Keyboard navigation when dropdown is open
  const handleDropdownKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (openPopover !== "dropdown") return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < sortedBranches.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case "Enter":
          e.preventDefault();
          if (sortedBranches[highlightedIndex]) {
            handleSelectBranch(sortedBranches[highlightedIndex].id);
          }
          break;
        case "Escape":
          e.preventDefault();
          setOpenPopover("none");
          break;
      }
    },
    [openPopover, sortedBranches, highlightedIndex, handleSelectBranch]
  );

  // Get checkpoint info
  const checkpoints = currentBranch.commits.filter((c) => c.message);
  const versionNumber = checkpoints.length;
  const isViewingHistory =
    commitId &&
    commitId !== currentBranch.headCommitId &&
    checkpoints.some((c) => c.id === commitId);

  // Get display info for current branch
  const getDisplayName = (branch: {
    id: string;
    name: string;
    commits?: readonly { message: string | null }[];
  }): ReactNode => {
    if (branch.name === "main") {
      const checkpointCount =
        branch.commits?.filter((c) => c.message).length ?? 0;
      return checkpointCount > 0 ? `v${checkpointCount}` : "Live";
    }
    return <StreamingText text={branch.name} branchId={branch.id} />;
  };

  const getTimestampInfo = (branch: {
    name: string;
    createdAt: number;
    lastChatEntryAt: number | null;
  }): { prefix: string; date: string } => {
    const timestamp = branch.lastChatEntryAt ?? branch.createdAt;
    const { date } = formatVersionDate(timestamp);
    const prefix = branch.name === "main" ? "pushed live" : "edited";
    return { prefix, date };
  };

  const isLastBranch = branchSummaries.length <= 1;

  const handleDeleteBranch = () => {
    if (isLastBranch) {
      setOpenPopover("none");
      deleteLastBranch.confirm(currentBranch.id, spaceId);
      return;
    }
    z.mutate(sharedMutators.deleteBranch({ branchId: currentBranch.id }));
    navigate(routes.space.workflow, {
      spaceId,
      workflowId: workflow.id,
      branchId: mainBranch?.id ?? "",
    });
    setOpenPopover("none");
  };

  const { pushLive } = usePushLive({
    branchId: currentBranch.id,
    spaceId,
    onAccept: () => setOpenPopover("none"),
    onDeploying,
    onMerging,
  });
  const handlePushToLive = () => {
    setOpenPopover("none");
    setPreflightOpen(true);
  };

  const handleSyncGit = () => {
    setOpenPopover("none");
    z.mutate(sharedMutators.syncGit({ spaceId, action: "sync" }));
  };

  const handleSaveToHistory = () => {
    z.mutate(sharedMutators.saveToHistory({ branchId: currentBranch.id }));
    setOpenPopover("none");
  };

  const getHistoryUrl = useCallback(
    (historyCommitId: string) => {
      return makeUrl(routes.space.workflow, {
        spaceId,
        workflowId: workflow.id,
        branchId: currentBranch.id,
        commitId: historyCommitId,
      });
    },
    [currentBranch, spaceId, workflow.id]
  );

  const getHeadUrl = useCallback(() => {
    return makeUrl(routes.space.workflow, {
      spaceId,
      workflowId: workflow.id,
      branchId: currentBranch.id,
      commitId: "",
    });
  }, [currentBranch, spaceId, workflow.id]);

  const handleDownloadChat = useCallback(() => {
    const label =
      currentBranch.name === "main"
        ? `v${currentBranch.commits.filter((c) => c.message).length}`
        : currentBranch.name;
    setOpenPopover("none");
    downloadChatAsJson(
      chatEntries,
      `${workflow.name}-${label}`,
      externalApiUrl
    ).catch(() => {
      showToast({
        icon: "circle-off",
        message: "Failed to download chat",
        buttonLabel: "Dismiss",
      });
    });
  }, [chatEntries, currentBranch, showToast, workflow.name, externalApiUrl]);

  const handleDownloadCode = useCallback(() => {
    setDownloadingCode(true);
    setOpenPopover("none");
  }, []);

  const handleForkAsBranch = useCallback(() => {
    const targetCommitId = commitId || currentBranch.headCommitId;
    if (!targetCommitId) return;

    const branchId = nanoid(12);
    const chatId = nanoid(12);
    const viewedCheckpoint = commitId
      ? currentBranch.commits.find((c) => c.id === commitId)
      : null;

    let name: string;
    if (isMainBranch) {
      const checkpoints = currentBranch.commits.filter((c) => c.message);
      const versionIndex = viewedCheckpoint
        ? checkpoints.indexOf(viewedCheckpoint) + 1
        : checkpoints.length;
      name = `Fork of ${versionIndex > 0 ? `v${versionIndex}` : "main"}`;
    } else {
      name = `Fork of ${currentBranch.name}`;
    }

    z.mutate(
      sharedMutators.createBranch({
        branchId,
        commitId: nanoid(12),
        chatId,
        workflowId: workflow.id,
        name,
        sourceCommitId: targetCommitId,
      })
    );
    savePlanMode(chatId, planModeBranchDefault);
    navigate(routes.space.workflow, {
      spaceId,
      workflowId: workflow.id,
      branchId,
      commitId: "",
    });
    setOpenPopover("none");
  }, [
    commitId,
    currentBranch,
    isMainBranch,
    planModeBranchDefault,
    spaceId,
    workflow.id,
    z,
  ]);

  const headCommit = currentBranch.commits.find(
    (c) => c.id === currentBranch.headCommitId
  );
  const hasUnsavedChanges = headCommit && !headCommit.message;
  const sortedCheckpoints = [...checkpoints].sort(
    (a, b) => b.createdAt - a.createdAt
  );

  const currentBranchContributors = useMemo(
    () => getContributors(currentBranch),
    [currentBranch]
  );
  const currentTimestamp = getTimestampInfo(currentBranch);

  // Get historical commit info when viewing history
  const historicalCommit = isViewingHistory
    ? currentBranch.commits.find((c) => c.id === commitId)
    : null;
  const historicalVersionIndex = historicalCommit
    ? sortedCheckpoints.findIndex((c) => c.id === historicalCommit.id)
    : -1;
  const historicalVersionNumber =
    historicalVersionIndex >= 0 ? versionNumber - historicalVersionIndex : null;
  const historicalTimestamp = historicalCommit
    ? formatVersionDate(historicalCommit.createdAt)
    : null;

  return (
    <Wrapper ref={containerRef} onKeyDown={handleDropdownKeyDown} tabIndex={-1}>
      {/* Version Dropdown */}
      <DropdownPopover
        isOpen={openPopover === "dropdown"}
        direction="down"
        popoverRef={popoverRef}
        popoverAnchorName={popoverAnchorName.current}
        trigger={(props) => (
          <SelectorBar
            {...props}
            onMouseDown={() => {
              playClick();
              if (openPopover !== "dropdown") {
                setDropdownMode("versions");
                setHighlightedIndex(
                  sortedBranches.findIndex((b) => b.id === currentBranchId)
                );
              }
              setOpenPopover(openPopover === "dropdown" ? "none" : "dropdown");
            }}
          >
            {isViewingHistory ? (
              <Tooltip content="Not live">
                <HistoryIndicator />
              </Tooltip>
            ) : isMainBranch ? (
              <Tooltip content={isPaused ? "Paused" : "Live"}>
                <LiveIndicator paused={isPaused} />
              </Tooltip>
            ) : isCurrentBranchAutofix ? (
              <Tooltip content="Autofix">
                <Icon name="radar" size={16} color="var(--text-primary)" />
              </Tooltip>
            ) : isCurrentBranchAutoImprove ? (
              <Tooltip content="Autotune">
                <Icon
                  name="audio-waveform"
                  size={16}
                  color="var(--text-primary)"
                />
              </Tooltip>
            ) : (
              <Tooltip content="Branch">
                <Icon name="git-branch" size={16} color="var(--text-primary)" />
              </Tooltip>
            )}
            <SelectorContent>
              {isViewingHistory && historicalVersionNumber !== null ? (
                <>
                  <Text
                    variant="text-regular"
                    color="var(--text-primary)"
                    ellipsis
                  >
                    v{historicalVersionNumber}
                  </Text>
                  <Text variant="text-small" color="var(--text-secondary)">
                    {historicalTimestamp?.date}
                  </Text>
                </>
              ) : (
                <>
                  <Text
                    variant="text-regular"
                    color="var(--text-primary)"
                    ellipsis
                  >
                    {getDisplayName(currentBranch)}
                  </Text>
                  <Text variant="text-small" color="var(--text-secondary)">
                    {isDeploying
                      ? "Deploying…"
                      : isMerging
                        ? "Merging live changes…"
                        : isMainBranch && isPaused && workflow.pausedAt != null
                          ? `paused ${formatVersionDate(workflow.pausedAt).date}`
                          : `${currentTimestamp.prefix} ${currentTimestamp.date}`}
                  </Text>
                </>
              )}
            </SelectorContent>
            {!isDeletedOnBranch && hasChangesToPush && isGitSynced && (
              <Tooltip
                content={
                  canSync
                    ? isSyncing
                      ? "Syncing…"
                      : "Push this draft to the git remote and merge it there"
                    : "You don’t have permission to sync"
                }
              >
                <SyncButton
                  disabled={!canSync || isSyncing}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSyncGit();
                  }}
                >
                  <Icon name="refresh-ccw" size={12} color="white" />
                  <Text variant="text-small-md" color="white">
                    Sync
                  </Text>
                </SyncButton>
              </Tooltip>
            )}
            {!isDeletedOnBranch && hasChangesToPush && !isGitSynced && (
              <Tooltip
                content={
                  canPushLive
                    ? "Your changes are ready"
                    : "You don’t have permission to push live"
                }
              >
                <PushLiveButton
                  style={
                    canPushLive
                      ? undefined
                      : { opacity: 0.5, cursor: "default" }
                  }
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    if (!canPushLive) return;
                    playClick();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!canPushLive) return;
                    handlePushToLive();
                  }}
                >
                  <Icon name="rocket" size={12} color="white" />
                  <Text variant="text-small-md" color="white">
                    Push live
                  </Text>
                </PushLiveButton>
              </Tooltip>
            )}
            {openPr &&
              (() => {
                const isGitlab = openPr.provider === "gitlab";
                const label = isGitlab
                  ? `MR !${openPr.prNumber}`
                  : `PR #${openPr.prNumber}`;
                return (
                  <Tooltip
                    content={`View ${isGitlab ? "merge" : "pull"} request: ${openPr.title}`}
                  >
                    <PrBadge
                      href={openPr.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Icon name="git-branch" size={12} />
                      <Text variant="text-small-md">{label}</Text>
                    </PrBadge>
                  </Tooltip>
                );
              })()}
            {!openPr &&
              isGitSynced &&
              !isMainBranch &&
              // The space branch row lands before the sync task has pushed the
              // branch, so offering the link mid-sync points at a ref the
              // remote doesn't have yet.
              !isSyncing &&
              webhookReady &&
              spaceGitBranch &&
              gitRemote?.remoteUrl &&
              (() => {
                const url = createPullRequestUrl(
                  gitRemote.remoteUrl,
                  spaceGitBranch.name
                );
                if (!url) return null;
                return (
                  <Tooltip
                    content={`Create a ${url.isGitlab ? "merge" : "pull"} request for ${spaceGitBranch.name}`}
                  >
                    <PrBadge
                      href={url.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Icon name="git-branch" size={12} />
                      <Text variant="text-small-md">
                        {url.isGitlab ? "Create MR" : "Create PR"}
                      </Text>
                    </PrBadge>
                  </Tooltip>
                );
              })()}
            {isMainBranch && isPaused && (
              <Tooltip
                content={
                  canPushLive
                    ? "Resume schedules and handling web requests"
                    : "You don’t have permission to resume"
                }
              >
                <ResumeButton
                  style={
                    canPushLive
                      ? undefined
                      : { opacity: 0.5, cursor: "default" }
                  }
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    if (!canPushLive) return;
                    playClick();
                    handleResume();
                  }}
                >
                  <Icon name="play" size={12} color="var(--text-primary)" />
                  <Text variant="text-small-md" color="var(--text-primary)">
                    Resume
                  </Text>
                </ResumeButton>
              </Tooltip>
            )}
            {currentBranchContributors.length > 0 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  height: 16,
                }}
              >
                <FacePile
                  itemSize={16}
                  outlineWidth={2}
                  maxItems={3}
                  cutout
                  items={currentBranchContributors.map((user) => {
                    const isPresent =
                      presentUserBranches.get(user.id)?.has(currentBranchId) ??
                      false;
                    return {
                      id: user.id,
                      label: userDisplayName(user),
                      bare: !isPresent,
                      node: (
                        <Avatar
                          size={16}
                          user={user}
                          isPresent={isPresent}
                          bare={!isPresent}
                        />
                      ),
                    };
                  })}
                />
              </div>
            )}
            {/* More menu button */}
            <ActionPopover
              isOpen={openPopover === "actions"}
              direction="down"
              popoverRef={popoverRef}
              popoverAnchorName={actionAnchorName.current}
              trigger={(actionProps) => (
                <MoreButton
                  {...actionProps}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    playClick();
                    setOpenPopover(
                      openPopover === "actions" ? "none" : "actions"
                    );
                  }}
                >
                  <Icon name="more" size={12} color="var(--text-secondary)" />
                </MoreButton>
              )}
            >
              {!isMainBranch && isGitSynced && (
                <MenuItem
                  disabled={!canSync || isSyncing}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={handleSyncGit}
                >
                  <Icon
                    name="refresh-ccw"
                    size={16}
                    color="var(--text-primary)"
                  />
                  <Text variant="text-regular" color="var(--text-primary)">
                    {isSyncing ? "Syncing…" : "Sync to git"}
                  </Text>
                </MenuItem>
              )}
              {!isMainBranch && !isGitSynced && (
                <MenuItem
                  disabled={!canPushLive}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={handlePushToLive}
                >
                  <Icon name="rocket" size={16} color="var(--text-primary)" />
                  <Text variant="text-regular" color="var(--text-primary)">
                    Push live
                  </Text>
                </MenuItem>
              )}
              {isMainBranch && !isPaused && (
                <MenuItem
                  disabled={!canPushLive}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={() => {
                    setOpenPopover("none");
                    setPauseConfirmOpen(true);
                  }}
                >
                  <Icon
                    name="circle-pause"
                    size={16}
                    color="var(--text-primary)"
                  />
                  <Text variant="text-regular" color="var(--text-primary)">
                    Pause workflow
                  </Text>
                </MenuItem>
              )}
              {isMainBranch && isPaused && (
                <MenuItem
                  disabled={!canPushLive}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={handleResume}
                >
                  <Icon name="play" size={16} color="var(--text-primary)" />
                  <Text variant="text-regular" color="var(--text-primary)">
                    Resume workflow
                  </Text>
                </MenuItem>
              )}
              <MenuItem
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => {
                  setDropdownMode("history");
                  setOpenPopover("dropdown");
                }}
              >
                <Icon name="history" size={16} color="var(--text-primary)" />
                <Text variant="text-regular" color="var(--text-primary)">
                  History
                </Text>
              </MenuItem>
              <MenuItem
                onMouseDown={(e) => e.stopPropagation()}
                onClick={handleDownloadChat}
              >
                <Icon
                  name="message-circle"
                  size={16}
                  color="var(--text-primary)"
                />
                <Text variant="text-regular" color="var(--text-primary)">
                  Download chat
                </Text>
              </MenuItem>
              <MenuItem
                onMouseDown={(e) => e.stopPropagation()}
                onClick={handleDownloadCode}
              >
                <Icon
                  name="square-code"
                  size={16}
                  color="var(--text-primary)"
                />
                <Text variant="text-regular" color="var(--text-primary)">
                  Download code
                </Text>
              </MenuItem>
              <MenuItem
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => {
                  const gitUrl = `${externalApiUrl}${spaceGitPath(spaceSlug)}`;
                  void writeToClipboard(gitUrl);
                  setOpenPopover("none");
                }}
              >
                <Icon name="link-2" size={16} color="var(--text-primary)" />
                <Text variant="text-regular" color="var(--text-primary)">
                  Copy Git URL
                </Text>
              </MenuItem>
              <MenuItem
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => {
                  navigate(routes.modal.storage, {
                    spaceId,
                    branchId: currentBranch.id,
                  });
                  setOpenPopover("none");
                }}
              >
                <Icon name="box" size={16} color="var(--text-primary)" />
                <Text variant="text-regular" color="var(--text-primary)">
                  Browse storage
                </Text>
              </MenuItem>
              <MenuItem
                disabled={!canWrite}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={handleForkAsBranch}
              >
                <Icon name="git-branch" size={16} color="var(--text-primary)" />
                <Text variant="text-regular" color="var(--text-primary)">
                  Fork as branch
                </Text>
              </MenuItem>
              {!isMainBranch && (
                <>
                  <MenuDivider />
                  <MenuItem
                    disabled={!canWrite}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={handleDeleteBranch}
                  >
                    <Icon name="trash" size={16} color="var(--text-primary)" />
                    <Text variant="text-regular" color="var(--text-primary)">
                      Delete branch
                    </Text>
                  </MenuItem>
                </>
              )}
            </ActionPopover>
          </SelectorBar>
        )}
      >
        {dropdownMode === "versions" ? (
          <>
            <DropdownHeader>
              <Text variant="text-small" color="var(--text-secondary)">
                Versions and branches
              </Text>
            </DropdownHeader>
            {sortedBranches.map((branch, index) => {
              const isSelected = branch.id === currentBranchId;
              const isHighlighted = index === highlightedIndex;
              const isBranchMain = branch.name === "main";
              const branchUser = branch.user;
              const branchTimestamp = getTimestampInfo(branch);
              const branchUrl = getBranchUrl(branch.id);
              const isAutofixBranch = workflow.autofixBranchId === branch.id;
              const isAutoImproveBranch =
                workflow.autoImproveBranchId === branch.id;

              return (
                <VersionRowLink
                  key={branch.id}
                  data-selected={isSelected || undefined}
                  data-highlighted={isHighlighted || undefined}
                  to={branchUrl}
                  onMouseDown={() => setOpenPopover("none")}
                >
                  {isBranchMain ? (
                    <Tooltip content={isPaused ? "Paused" : "Live"}>
                      <LiveIndicator paused={isPaused} />
                    </Tooltip>
                  ) : isAutofixBranch ? (
                    <Tooltip content="Autofix">
                      <Icon
                        name="radar"
                        size={16}
                        color="var(--text-primary)"
                      />
                    </Tooltip>
                  ) : isAutoImproveBranch ? (
                    <Tooltip content="Autotune">
                      <Icon
                        name="audio-waveform"
                        size={16}
                        color="var(--text-primary)"
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip content="Branch">
                      <Icon
                        name="git-branch"
                        size={16}
                        color="var(--text-primary)"
                      />
                    </Tooltip>
                  )}
                  <VersionRowContent>
                    <Text variant="text-regular" ellipsis>
                      {getDisplayName(branch)}
                    </Text>
                    <Text variant="text-small" color="var(--text-secondary)">
                      {isBranchMain && isPaused && workflow.pausedAt != null
                        ? `paused ${formatVersionDate(workflow.pausedAt).date}`
                        : `${branchTimestamp.prefix} ${branchTimestamp.date}`}
                    </Text>
                    {isAutofixBranch && <AutofixBadge>Autofix</AutofixBadge>}
                    {isAutoImproveBranch && (
                      <AutoImproveBadge>Autotune</AutoImproveBadge>
                    )}
                    {isSelected && !isViewingHistory && (
                      <ViewingBadge>Viewing</ViewingBadge>
                    )}
                  </VersionRowContent>
                  {isAutofixBranch || isAutoImproveBranch ? (
                    <StatusIndicator
                      state={branch.respondingAt ? "building" : "ready"}
                      focused
                      size={16}
                    />
                  ) : (
                    (() => {
                      const contributors = getContributors(branch);
                      if (contributors.length > 0) {
                        return (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              height: 16,
                            }}
                          >
                            <FacePile
                              itemSize={16}
                              outlineWidth={2}
                              maxItems={3}
                              cutout
                              items={contributors.map((user) => {
                                const isPresent =
                                  presentUserBranches
                                    .get(user.id)
                                    ?.has(branch.id) ?? false;
                                return {
                                  id: user.id,
                                  label: userDisplayName(user),
                                  bare: !isPresent,
                                  node: (
                                    <Avatar
                                      size={16}
                                      user={user}
                                      isPresent={isPresent}
                                      bare={!isPresent}
                                    />
                                  ),
                                };
                              })}
                            />
                          </div>
                        );
                      }
                      return branchUser ? (
                        <Avatar
                          size={16}
                          user={branchUser}
                          isPresent={
                            presentUserBranches
                              .get(branchUser.id)
                              ?.has(branch.id) ?? false
                          }
                        />
                      ) : null;
                    })()
                  )}
                </VersionRowLink>
              );
            })}
          </>
        ) : (
          <>
            <DropdownHeader>
              <Text variant="text-small" color="var(--text-secondary)">
                {isMainBranch ? "Version history" : "Branch history"}
              </Text>
            </DropdownHeader>
            {hasUnsavedChanges &&
              (isViewingHistory ? (
                <VersionRowLink
                  to={getHeadUrl()}
                  onMouseDown={() => setOpenPopover("none")}
                >
                  <Icon name="pencil" size={16} color="var(--text-primary)" />
                  <VersionRowContent>
                    <Text variant="text-regular" ellipsis>
                      Unsaved changes
                    </Text>
                  </VersionRowContent>
                </VersionRowLink>
              ) : (
                <VersionRow
                  disabled={!canWrite}
                  onClick={() => {
                    if (!canWrite) return;
                    playClick();
                    handleSaveToHistory();
                  }}
                >
                  <Icon name="save" size={16} color="var(--text-primary)" />
                  <VersionRowContent>
                    <Text variant="text-regular" ellipsis>
                      Save to history
                    </Text>
                  </VersionRowContent>
                </VersionRow>
              ))}
            {sortedCheckpoints.length === 0 && !hasUnsavedChanges ? (
              <VersionRow data-selected="true">
                <Text variant="text-small" color="var(--text-secondary)">
                  No history yet
                </Text>
              </VersionRow>
            ) : (
              sortedCheckpoints.map((checkpoint, index) => {
                const isCurrentVersion = checkpoint.id === commitId;
                const isHead =
                  !isViewingHistory &&
                  checkpoint.id === currentBranch.headCommitId;
                const version = versionNumber - index;
                const { date } = formatVersionDate(checkpoint.createdAt);
                const isViewing = isCurrentVersion || isHead;
                const historyUrl = getHistoryUrl(checkpoint.id);

                if (isViewing) {
                  return (
                    <VersionRow key={checkpoint.id} data-selected="true">
                      <Icon
                        name="history"
                        size={16}
                        color="var(--text-primary)"
                      />
                      <VersionRowContent>
                        <Text variant="text-regular" ellipsis>
                          {isMainBranch
                            ? checkpoint.message
                              ? `v${version}${META_SEP}${checkpoint.message}`
                              : `v${version}`
                            : checkpoint.message || `Version ${version}`}
                        </Text>
                        <Text
                          variant="text-small"
                          color="var(--text-secondary)"
                        >
                          {isMainBranch ? "pushed live" : "saved"} {date}
                        </Text>
                        <ViewingBadge>Viewing</ViewingBadge>
                      </VersionRowContent>
                    </VersionRow>
                  );
                }

                return (
                  <VersionRowLink
                    key={checkpoint.id}
                    to={historyUrl}
                    onMouseDown={() => setOpenPopover("none")}
                  >
                    <Icon
                      name="history"
                      size={16}
                      color="var(--text-primary)"
                    />
                    <VersionRowContent>
                      <Text variant="text-regular" ellipsis>
                        {isMainBranch
                          ? checkpoint.message
                            ? `v${version}${META_SEP}${checkpoint.message}`
                            : `v${version}`
                          : checkpoint.message || `Version ${version}`}
                      </Text>
                      <Text variant="text-small" color="var(--text-secondary)">
                        {isMainBranch ? "pushed live" : "saved"} {date}
                      </Text>
                    </VersionRowContent>
                  </VersionRowLink>
                );
              })
            )}
          </>
        )}
      </DropdownPopover>
      {deleteLastBranch.modal}
      <ConfirmModal
        open={pauseConfirmOpen}
        onClose={() => setPauseConfirmOpen(false)}
        onConfirm={handleConfirmPause}
        title="Pause workflow?"
        description="Schedules will stop running, incoming web requests will be rejected, and no manual runs can start. You can resume at any time."
        confirmLabel="Pause"
      />
      {preflightOpen && (
        <PushLivePreflight
          workflowId={workflow.id}
          branchId={currentBranch.id}
          onProceed={() => {
            setPreflightOpen(false);
            pushLive(currentBranch, mainBranch?.id);
          }}
          onCancel={() => setPreflightOpen(false)}
        />
      )}
    </Wrapper>
  );
}
